import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import usePathPattern from "../hooks/usePathPattern";
import { FullScreenLoader, HandleScrollPosition, OnboardingBanner, OnboardingModal, PostOnboardingBanner } from "../components";
import { AuthContext } from "../contexts/AuthContext/AuthContext";
import { ToastContext } from "../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../models";
import useHomeRoute from "../hooks/useHomeRoute";
import useLoader from "../hooks/useLoader";
import { getUser, setCurrentUserNull } from "../redux/slices/CurrentUserSlice";
import { AppDispatch, RootState } from "../redux/store";
import {
  AcceptInvitation,
  AddEditTemplate,
  AddNotification,
  AdminFinanceScreen,
  CreateRequestScreen,
  EmailListing,
  EmailVerified,
  FeesScreen,
  ForgotPassword,
  GuestLoginScreen,
  GuestVerifyPhone,
  GuestViewRequest,
  IntegrateConfigure,
  Landing,
  NotFoundScreen,
  NotificationListing,
  OfficeDetailsScreen,
  OfficeInfo,
  OfficesScreen,
  OfficeTeam,
  PatientDashboard,
  PatientViewRequest,
  PssNavigate,
  ReleaseNotesList,
  ReleaseNotesUpdate,
  FeatureFlagsList,
  MailerSendTemplatesList,
  ResetPassword,
  ServiceScreen,
  SingleOffice,
  SubmitRequest,
  ThankYou,
  UserScreen,
  VerifyEmail,
  VerifyPhone,
  ViewRequest,
} from "../screens";
import FinanceScreen from "../screens/NewFinanceScreen/FinanceScreen";
import PrivacyPolicyPage from "../screens/Auth/PrivacyPolicyPage";
import TermsAndConditionsPage from "../screens/Auth/TermsAndConditionsPage";
import OfficePatients from "../screens/OfficePatients/OfficePatients";
import { apiService } from "../services/api.service";
import { UserType } from "../services/user.service";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../utils/constants";
import ProtectedOnboardingRoute from "./ProtectedOnboardingRoute";
import ProtectedRoute from "./ProtectedRoute";
import CreateAccount from "../screens/Onboarding/CreateAccount";
import OfficePlan from "../screens/OfficePlan/OfficePlan";
import { PatientInvitation } from "../screens/PatientInvitation";
import HeaderNew from "../components/Header/HeaderNew";
import LeftMenu from "../components/Menu/LeftMenu";
import Doctors from "../screens/Doctors";
import { ContactsScreen } from "../screens/ContactsScreen";
import ViewPatient from "../screens/ViewPatient/ViewPatient";
import Activate from "../screens/Activate";
import SearchScreen from "../screens/SearchScreen/SearchScreen";
import FormsScreen from "../screens/FormsScreen/FormsScreen";
import PatientRequests from "../screens/PatientDashboard/PatientRequests";
import CreateGuestAccount from "../screens/Onboarding/CreateGuestAccount";
import PatientProfile from "../screens/PatientDashboard/PatientProfile";
import PatientAccountSettings from "../screens/PatientDashboard/PatientAccountSettings";
import PatientDocuments from "../screens/PatientDocuments/PatientDocuments";
import PatientReceipts from "../screens/PatientReceipts/PatientReceipts";
import CreatePatientRequest from "../screens/CreatePatientRequest/CreatePatientRequest";
import { getUserLastAccessDate } from "../redux/slices/UserSlice";
import PatientPlan from "../screens/PatientPlan/PatientPlan";
import BlockRejectedOfficeModal from "../components/Modal/BlockRejectedOfficeModal";
import { loadOnboardingStatus, openModal, setCurrentStep } from "../redux/slices/OfficeOnboardingSlice";
import { enrichOfficeWithV2Data } from "../redux/slices/OfficeSlice";
import RouteValidator from "../components/RouteValidator";
import ExternalRedirect from "../components/ExternalRedirect";
import MaintenanceBanner from "../screens/Maintenance/MaintenanceBanner";
import BadGatewayScreen from "../screens/NotFound/BadGateway";
import ServiceUnavailableScreen from "../screens/NotFound/ServiceUnavailable";
import GatewayTimeoutScreen from "../screens/NotFound/GatewayTimeout";
import InternalServerError from "../screens/NotFound/InternaServerError";
import PhysicianSignupPage from "../screens/Auth/PhysicianSignupPage";
import PatientSignupPage from "../screens/Auth/PatientSignupPage";
import AuthFooter from "../components/AuthFooter/AuthFooter";
import HomePageHeader from "../screens/Onboarding/HomePageHeader";

export const userPaths = [
  { path: "/offices/:id/request" },
  { path: "/offices/:id/services" },
  { path: "/offices/:id/fees" },
  { path: "/offices/:id/plans" },
  { path: "/offices/:id/requests/:requestId" },
  { path: "/offices/:id/patients" },
  { path: "/offices/:id/patients/:patientId" },
  { path: "/offices/:id/forms" },
];

export const patientPaths = [
  { path: "/patients/:patientId/dashboard" },
  { path: "/patients/:patientId/requests" },
  { path: "/patients/:patientId/requests/:requestId" },
  { path: "/patients/:id/documents" },
  { path: "/patients/:id/plans" },
  { path: "/patients/:id/receipts" },
  { path: "/patients/:id/plans" },
  // { path: "/patients/:id/resources" },
];

export const adminPaths = [
  { path: "/offices/" },
  { path: "/users" },
  { path: "/services" },
  { path: "/fees" },
  { path: "/finance" },
  { path: "/search" },
  { path: "/all-patients" },
  { path: "/forms" },
  { path: "/offices/:id/requests" },
  { path: "/offices/:id/services" },
  { path: "/offices/:id/team" },
  { path: "/offices/:id/patients" },
  { path: "/offices/:id/finance" },
  { path: "/offices/:id/office-info" },
  { path: "/offices/:id/requests/:requestId" },
  { path: "/offices/:id/requests/create-request" },
  { path: "/offices/:id/plans" },
  { path: "/offices/:id/contacts" },
  { path: "/offices/:id/patients/:patientId" },
  { path: "/offices/:id/forms" },
  { path: "/offices/:id/val" },
];
export const systemAdminPaths = [
  { path: "/offices/" },
  { path: "/users" },
  { path: "/services" },
  { path: "/fees" },
  { path: "/all-patients" },
  { path: "/search" },
  { path: "/finance" },
  { path: "/forms" },
  { path: "/feature-flags" },
  { path: "/mailersend-templates" },
];
export const hideLoginInHeaderPaths = ["/guest/view-request", "/guest/verify-phone", "invitation"];

const RootNavigator = () => {
  const auth = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const navigate = useNavigate();
  const [initialised, setInitialised] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const { isCompleted, currentStep, totalSteps, isModalOpen, isLoading } = useSelector((state: RootState) => state.officeOnboarding);
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const location = useLocation();
  const redirectPath = localStorage.getItem("redirectPath");
  const dispatch: AppDispatch = useDispatch();
  const loader = useLoader();
  const { homeLink } = useHomeRoute();
  const match = usePathPattern();

  const isPatientInvitation =
    location.pathname.includes("/patient-invitation") ||
    location.pathname.includes("/guest") ||
    location.pathname.includes("/reset-password") ||
    location.pathname.includes("/invitation");

  const noHeader =
    location?.pathname === "/login" ||
    location.pathname === "/" ||
    location.pathname === "/patient" ||
    location.pathname === "/forgot-password" ||
    // location.pathname === "/reset-password" ||
    // location.pathname?.startsWith("/guest/") ||
    location.pathname === "/onboarding/office" ||
    location.pathname === "/doctors" ||
    location.pathname === "/activate" ||
    location.pathname === "/patients-new" ||
    location.pathname === "/terms-of-use" ||
    location.pathname === "/terms-and-conditions" ||
    location.pathname === "/privacy-policy";

  const showLeftMenu = auth?.isAuthenticated && !noHeader && !["/404", "/502", "/503", "/504", "/500"].includes(location?.pathname);

  const isSystemAdminPath = () => {
    return systemAdminPaths.filter(it => it.path === match.path).length;
  };
  const isUserPath = () => {
    return userPaths.filter(it => it.path === match.path).length > 0;
  };

  const isPatientPath = () => {
    return patientPaths.filter(it => it.path === match.path).length > 0;
  };

  const onClick = (val: boolean) => {
    setShowMenu(val);
  };

  const callUserGuiding = async () => {
    const windowObj = window as any;
    if (currentUser && windowObj && windowObj?.userGuiding && windowObj?.userGuiding?.identify) {
      const result = await dispatch(getUserLastAccessDate({ userId: currentUser.userId }));
      windowObj?.userGuiding?.identify(currentUser.userId, {
        userType: currentUser.userType,
        company: {
          id: currentUser?.offices?.length ? currentUser?.offices[0].officeId : null,
        },
        lastAccessDate: result.payload,
      });
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    apiService.init(accessToken ? accessToken : "", refreshToken);
    if (!location.pathname.includes("invitation")) auth?.isAlreadyLoggedIn();
    setInitialised(true);
  }, []);

  useEffect(() => {
    if (location.pathname.includes("/patients") || location.pathname.includes("/offices")) {
      localStorage.setItem("redirectPath", location.pathname);
    }
    if (auth?.isAuthenticated === true && !location.pathname.includes("invitation")) {
      dispatch(getUser());
    } else {
      dispatch(setCurrentUserNull());
    }
  }, []);

  useEffect(() => {
    if (screenWidth < 1024 && showLeftMenu) {
      onClick(false);
    }
  }, [location.pathname, screenWidth, showLeftMenu]);

  useEffect(() => {
    const updateDimension = () => {
      if ((window.innerWidth < 1023 && screenWidth > 1023) || (window.innerWidth > 1023 && screenWidth < 1024)) setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", updateDimension);

    return () => {
      window.removeEventListener("resize", updateDimension);
    };
  }, [screenWidth]);

  useEffect(() => {
    if (
      initialised &&
      currentUser &&
      window.location.href.includes(`${window.location.origin}/patients`) &&
      currentUser?.userType !== UserType.Patient
    ) {
      // Preserve remembered email before clearing localStorage
      const rememberedEmail = localStorage.getItem("rememberedEmail");
      localStorage.clear();
      // Restore remembered email after clearing
      if (rememberedEmail) {
        localStorage.setItem("rememberedEmail", rememberedEmail);
      }
      navigate(location.pathname);
    }
  }, [initialised, location, currentUser]);

  useEffect(() => {
    const windowObj = window as any;
    if (currentUser && windowObj && windowObj?.userGuiding) {
      callUserGuiding();
    }
  }, [currentUser]);

  // Check for welcome modal flag and open modal on first login
  useEffect(() => {
    // Check flag first, before checking user (in case user loads later)
    const showWelcomeModal = localStorage.getItem("showWelcomeModal");

    if (showWelcomeModal === "true" && currentUser && auth?.isAuthenticated && currentUser.userType === UserType.OfficeAdmin) {
      // Set step to 0 (WelcomeStep) and open modal - don't check backend
      // Don't delete flag yet - let OnboardingModal delete it after confirming step 0
      dispatch(setCurrentStep(0));
      dispatch(openModal());
      // Don't load onboarding status - return early
      return;
    }

    // Load onboarding status for office-admin only (skip if welcome modal flag exists)
    // Only load when modal is NOT open - OnboardingModal handles loading when modal opens
    if (currentUser && auth?.isAuthenticated && currentUser.userType === UserType.OfficeAdmin && !isModalOpen) {
      const officeId = currentUser?.offices?.[0]?.officeId;
      // Only load if welcome modal flag doesn't exist
      if (officeId && showWelcomeModal !== "true") {
        dispatch(loadOnboardingStatus(officeId));
      }
    }
  }, [currentUser, auth?.isAuthenticated, dispatch, isModalOpen]);

  // Hybrid approach: Enrich currentOffice with v2 data (billingCompleted, bankCompleted) when needed
  useEffect(() => {
    if (
      currentUser &&
      auth?.isAuthenticated &&
      currentUser.userType === UserType.OfficeAdmin &&
      currentOffice &&
      currentOffice.officeId &&
      (currentOffice.billingCompleted === undefined || currentOffice.bankCompleted === undefined)
    ) {
      // Only fetch v2 data if we don't already have billingCompleted or bankCompleted
      dispatch(enrichOfficeWithV2Data({ officeId: currentOffice.officeId }));
    }
  }, [currentUser, auth?.isAuthenticated, currentOffice, dispatch]);

  if (!initialised) return null;

  let redirect = "";
  if (currentUser !== undefined && auth?.isAuthenticated) {
    if (currentUser?.userType === UserType.User && !isUserPath()) redirect = homeLink;
    else if (currentUser?.userType === UserType.Patient && !isPatientPath()) {
      redirect = redirectPath === "/" ? homeLink : redirectPath || "";
    } else if (currentUser?.userType === UserType.OfficeAdmin && isSystemAdminPath()) redirect = homeLink;
    else if (currentUser?.userType === UserType.Accounting && isSystemAdminPath()) redirect = homeLink;
    else if (currentUser?.userType === UserType.SystemAdmin && isSystemAdminPath()) {
      redirect = redirectPath === "/" ? "" : redirectPath || "";
      localStorage.removeItem("redirectPath");
    }
  }
  return (
    <>
      {!currentUser && <HomePageHeader />}
      <div className={`${!noHeader ? "main-container container" : ""}${!currentUser ? " mt-[100px]" : ""}`}>
        {currentUser && <MaintenanceBanner />}
        {/* Onboarding Banner - Show for office-admin only who haven't completed onboarding (not on welcome screen) */}
        {/* Only show if billing and bank info are not both completed */}
        {currentUser &&
          auth?.isAuthenticated &&
          currentUser.userType === UserType.OfficeAdmin &&
          !noHeader &&
          !isCompleted &&
          currentStep > 0 &&
          // Only show banner if billingCompleted and bankCompleted are defined (not undefined)
          currentOffice?.billingCompleted !== undefined &&
          currentOffice?.bankCompleted !== undefined &&
          !(currentOffice?.billingCompleted && currentOffice?.bankCompleted) && (
            <div className="px-4 py-3 relative z-10" data-onboarding-banner>
              <OnboardingBanner currentStep={currentStep} totalSteps={totalSteps} />
            </div>
          )}
        {/* Post-Onboarding Banner - Show after completion if needed (office-admin only) */}
        {/* Only show if billing and bank info are not both completed */}
        {currentUser &&
          auth?.isAuthenticated &&
          currentUser.userType === UserType.OfficeAdmin &&
          !noHeader &&
          isCompleted &&
          // Only show banner if billingCompleted and bankCompleted are defined (not undefined)
          currentOffice?.billingCompleted !== undefined &&
          currentOffice?.bankCompleted !== undefined &&
          !(currentOffice?.billingCompleted && currentOffice?.bankCompleted) && (
            <div className="px-4 py-3">
              <PostOnboardingBanner />
            </div>
          )}
        {!noHeader ? (
          <>
            {showLeftMenu ? (
              <span
                className={`humburger cursor-pointer ${showMenu ? "active" : ""}`}
                onClick={e => {
                  e.preventDefault();
                  onClick(!showMenu);
                }}
              >
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : null}

            {!isPatientInvitation && currentUser && <HeaderNew showLeftMenu={showLeftMenu && screenWidth < 1024} />}
            <HandleScrollPosition />
          </>
        ) : null}
        {loader ? <FullScreenLoader /> : null}
        <div className="flex relative">
          {showLeftMenu ? (
            <div className={`w-2/12 leftside-panel ${showMenu ? "leftside-panel-show" : "leftside-panel-hide"}`}>
              <LeftMenu screenWidth={screenWidth} />
            </div>
          ) : null}
          <div className={`maincontainer-panel ${showLeftMenu ? "w-10/12" : "w-full"} ${!noHeader ? "bg-white rounded-[30px]" : ""} relative`}>
            {/* Overlay to disable page when onboarding is not complete (office-admin only) */}
            {/* Only show overlay if billing and bank info are not both completed */}
            {currentUser &&
              auth?.isAuthenticated &&
              currentUser.userType === UserType.OfficeAdmin &&
              !isCompleted &&
              !noHeader &&
              !(currentOffice?.billingCompleted && currentOffice?.bankCompleted) && (
                <div
                  className="absolute inset-0 z-[5] cursor-pointer"
                  onClick={e => {
                    // Don't trigger if clicking on OnboardingBanner or modals/drawers (they're above this overlay)
                    const target = e.target as HTMLElement;
                    const clickedElement = target.closest(
                      "[data-onboarding-banner], [class*='z-20'], [class*='z-30'], [class*='z-40'], [class*='z-50']"
                    );
                    if (!clickedElement) {
                      dispatch(openModal());
                      toast?.openToast("Please complete onboarding to access this feature.", 3000, ToastVariants.Warn);
                    }
                  }}
                  style={{ pointerEvents: "auto" }}
                />
              )}
            <Routes>
              <Route path="/omgma" element={<ExternalRedirect url="https://landing.docnote.com/omgma" />} />
              <Route path="/omddhc2023" element={<Navigate to="/doctors" replace />} />
              <Route path="/omd" element={<Navigate to="/doctors" replace />} />
              <Route path="/demo" element={<Navigate to="/activate" replace />} />
              <Route path="/" element={auth?.isAuthenticated ? <Navigate to={homeLink} /> : <PhysicianSignupPage />} />
              <Route path="/login" element={!currentUser || !auth?.isAuthenticated ? <Landing /> : <Navigate to={homeLink} />} />
              {/* <Route path={"create-account"} element={<ProtectedOnboardingRoute />}>
                <Route index element={<CreateAccount />} />
              </Route> */}
              <Route path={"onboarding"} element={<ProtectedOnboardingRoute />}>
                <Route path={"office"} element={<SingleOffice />} />
                <Route path={"create-account"} element={<CreateAccount />} />
                <Route path={"create-guest-request"} element={<CreateGuestAccount />} />
                <Route path={"verify-email"} element={<VerifyEmail />} />
                <Route path={"verify-success"} element={<EmailVerified />} />
                <Route path={"submit-request"} element={<SubmitRequest />} />
                <Route path={"verify-phone"} element={<VerifyPhone />} />
                <Route path={"thank-you"} element={<ThankYou />} />
              </Route>
              <Route path="doctors" element={<Doctors />} />
              <Route path="activate" element={<Activate />} />
              <Route path="invitation/:invitationToken" element={<AcceptInvitation />} />
              <Route path="patient-invitation/:invitationToken" element={<PatientInvitation />} />
              <Route path="terms-of-use" element={<TermsAndConditionsPage />} />
              <Route path="terms-and-conditions" element={<Navigate to="/terms-of-use" replace />} />
              <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
              {/* accessible by patient user only */}
              <Route path="patients/:patientId" element={<ProtectedRoute userRoles={[UserType.Patient]} />}>
                <Route element={<RouteValidator isPatient />}>
                  <Route index element={<Navigate to="dashboard" />} />
                  <Route path="dashboard">
                    <Route index element={homeLink !== "" ? <PatientDashboard /> : <Navigate to={redirect} />} />
                    <Route path="profile" element={<PatientProfile />} />
                    <Route path="account-settings" element={<PatientAccountSettings />} />
                  </Route>
                  <Route path="documents" element={<PatientDocuments />} />
                  <Route path="receipts" element={<PatientReceipts />} />
                  <Route path="request/:patientRequestId">
                    <Route index element={homeLink !== "" ? <PatientViewRequest /> : <Navigate to={redirect} />} />
                  </Route>
                  <Route path="requests">
                    <Route index element={homeLink !== "" ? <PatientRequests /> : <Navigate to={redirect} />} />
                    <Route path=":patientRequestId" element={homeLink !== "" ? <PatientViewRequest /> : <Navigate to={redirect} />} />
                    <Route path="create-request" element={<CreatePatientRequest />} />
                  </Route>
                  <Route path="plans" element={<PatientPlan />} />
                </Route>
              </Route>
              {/* Only accessible by admin and office admin */}
              <Route path="offices" element={<ProtectedRoute userRoles={[UserType.SystemAdmin, UserType.OfficeAdmin]} />}>
                <Route index element={redirect === "" ? <OfficesScreen /> : <Navigate to={redirect} />} />
              </Route>
              {/* accessible by admin, office admin, accountant and office user */}
              <Route
                path="offices/:id"
                element={<ProtectedRoute userRoles={[UserType.SystemAdmin, UserType.OfficeAdmin, UserType.Accounting, UserType.User]} />}
              >
                {/* Validate the officeId before rendering children */}
                {/* <Route index element={redirect === "" ? <IntegrateConfigure isAdminPage /> : <Navigate to={redirect} />} /> */}
                <Route element={<RouteValidator />}>
                  <Route index element={<Navigate to="requests" />} />
                  <Route path="team" element={<OfficeTeam />} />
                  <Route path="patients">
                    <Route index element={redirect === "" ? <OfficePatients isAdminPage={false} /> : <Navigate to={redirect} />} />
                    <Route path={":patientId"}>
                      <Route index element={redirect === "" ? <ViewPatient /> : <Navigate to={redirect} />} />
                    </Route>
                  </Route>
                  <Route path="office-info" element={<OfficeInfo />} />
                  <Route path="pss-navigate" element={<PssNavigate />} />
                  <Route path="integrate-configure" element={<IntegrateConfigure isAdminPage={false} />} />
                  <Route path="finance" element={<FinanceScreen />} />
                  <Route path={"fees"} element={redirect === "" ? <FeesScreen isAdminPage={false} /> : <Navigate to={redirect} />} />
                  <Route path="requests">
                    <Route index element={<OfficeDetailsScreen />} />
                    <Route path="create-request" element={<CreateRequestScreen />} />
                    <Route path=":requestId" element={redirect === "" ? <ViewRequest /> : <Navigate to={redirect} />} />
                  </Route>
                  <Route path="services" element={<ServiceScreen isAdminPage={false} />} />
                  <Route path="resources" element={<NotFoundScreen />} />
                  <Route path="plans" element={<OfficePlan />} />
                  <Route path="contacts" element={<ContactsScreen />} />
                  <Route path={"forms"} element={redirect === "" ? <FormsScreen /> : <Navigate to={redirect} />} />
                </Route>
              </Route>
              {/* Only accessible by admin and office admin start */}
              <Route path={"fees"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <FeesScreen isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"finance"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <AdminFinanceScreen /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"users"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <UserScreen /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"services"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <ServiceScreen isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"search"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <SearchScreen /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"forms"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <FormsScreen isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              {/* Only accessible by admin and office admin end */}
              {/* Only accessible by guest */}
              <Route path="guest" element={<ProtectedOnboardingRoute />}>
                <Route path="login" element={<GuestLoginScreen />} />
                <Route path={"verify-phone"} element={<GuestVerifyPhone />} />
                <Route path="view-request" element={<GuestViewRequest />} />
              </Route>
              <Route path="physician" element={<Navigate to="/login" replace />} />
              <Route path="physician/signup" element={<Navigate to="/" replace />} />
              <Route path="patient" element={auth?.isAuthenticated ? <Navigate to={homeLink} /> : <PatientSignupPage />} />
              <Route path="patient/signup" element={<Navigate to="/patient" replace />} />
              <Route path="forgot-password" element={!auth?.isAuthenticated ? <ForgotPassword /> : <Navigate to={homeLink} />} />
              <Route path="reset-password" element={!auth?.isAuthenticated ? <ResetPassword /> : <Navigate to={homeLink} />} />
              <Route path="*" element={<Navigate to={"/404"} />} />
              <Route path="404" element={<NotFoundScreen />} />
              <Route path="500" element={<InternalServerError />} />
              <Route path="502" element={<BadGatewayScreen />} />
              <Route path="503" element={<ServiceUnavailableScreen />} />
              <Route path="504" element={<GatewayTimeoutScreen />} />
              {/* this is for Email templates */}
              <Route path={"template-listing"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <EmailListing isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"add-edit-template"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <AddEditTemplate isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"pss-navigate"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <PssNavigate isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"create-notification"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <AddNotification isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"notification-listing"} element={<ProtectedRoute userRoles={[UserType.OfficeAdmin, UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <NotificationListing isAdminPage /> : <Navigate to={redirect} />} />
              </Route>
              {/* Feature Flags & MailerSend Templates - Only accessible by SystemAdmin */}
              <Route path={"feature-flags"} element={<ProtectedRoute userRoles={[UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <FeatureFlagsList /> : <Navigate to={redirect} />} />
              </Route>
              <Route path={"mailersend-templates"} element={<ProtectedRoute userRoles={[UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <MailerSendTemplatesList /> : <Navigate to={redirect} />} />
              </Route>
              {/* Release Notes - Only accessible by SystemAdmin (superuser) */}
              <Route path={"version_info"} element={<ProtectedRoute userRoles={[UserType.SystemAdmin]} />}>
                <Route index element={redirect === "" ? <ReleaseNotesList /> : <Navigate to={redirect} />} />
                <Route path={"update"} element={redirect === "" ? <ReleaseNotesUpdate /> : <Navigate to={redirect} />} />
              </Route>
            </Routes>
          </div>
        </div>
      </div>
      {currentUser?.userType !== UserType.Patient && (
        <div className="mt-10 bg-[#111C3D] border-t border-white/20 ">
          <AuthFooter extraClasses={"footerContainer mx-auto"} />
        </div>
      )}
      <BlockRejectedOfficeModal />
      {/* Onboarding Modal */}
      <OnboardingModal />
      {/* <Footer /> */}
    </>
  );
};
export default RootNavigator;
